import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatSidebar, MobileDrawer } from "./ChatSidebar";
import { ChatComposer } from "./ChatComposer";
import { VoiceConversationErrorBoundary } from "./VoiceConversationErrorBoundary";
import { MessageBubble } from "./MessageBubble";
import { EmptyState } from "./EmptyState";
import { StarterPrompts } from "./StarterPrompts";
import { makeStreamingPlaceholder } from "./streamingMock";
import {
  streamChatAnswer,
  uploadChatDocument,
  postConversationCsat,
  fetchConversationHistory,
  fetchConversationMessages,
} from "./realApi";
import type { ChatEnvelope } from "./realApi";
import { readStoredAnswers } from "../../lib/storedAnswers";
import type { Conversation, Message, Citation } from "./types";

// Lazy — voice conversation mode pulls in @ricky0123/vad-web + onnxruntime-web
// (~500KB), which would otherwise bloat the main bundle for every user, most
// of whom never open voice mode. Only fetched on first use of the composer's
// voice-conversation button.
const VoiceConversationOverlay = lazy(() => import("./VoiceConversationOverlay"));

/** Map the answer-envelope citations (kb_id/source/tier) to the chat's display Citation chips. */
function mapCitations(cites: ChatEnvelope["citations"]): Citation[] {
  return (cites || []).map((c) => ({
    label: String(c.source || "source"),
    url: "#",
    kind: "internal" as const,
  }));
}

/**
 * Beyond Sure — main chat surface.
 *
 * The shell:
 *   - Desktop: collapsible left sidebar | centered message column (~720px) | quiet
 *     near-static background. The composer is centered at the bottom, glassy.
 *   - Mobile: NO bottom tab bar. Top-left hamburger opens MobileDrawer.
 *     Composer fixed near the bottom, safe-area-inset aware.
 *
 * Phase 1 ships:
 *   - Sidebar (mock conversations, new chat, account footer)
 *   - Empty state (warm + name-aware + persona-aware tagline)
 *   - Token-by-token streaming replies (mock)
 *   - Markdown rendering
 *   - Streaming caret + reduced-motion safe entry
 *
 * Phase 2 will add rich message blocks (recommendation / comparison /
 * truth-audit / OCR card) + thinking-dropdown integration.
 * Phase 3 will add tailored starter prompts + real upload + history wiring.
 */

export function ChatScreen() {
  const navigate = useNavigate();
  // Onboarding payload (drives empty state name, tagline persona).
  const answers = useMemo(() => readStoredAnswers(), []);

  // Sidebar conversations + the in-view one. Real history (GET /api/history), owner-scoped —
  // a brand-new account correctly starts with an empty list, not seeded fake threads.
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const refreshHistory = () => void fetchConversationHistory().then(setConversations);
  useEffect(() => {
    refreshHistory();
  }, []);
  const [activeId, setActiveId] = useState<string | null>(null);
  // BQ7-2: the REAL backend conversation id (from the answer envelope) — enables conversation CSAT.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [csatDone, setCsatDone] = useState(false);

  // Sidebar UI.
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Conversation state.
  const [messages, setMessages] = useState<Message[]>([]);
  const [busy, setBusy] = useState(false);
  // L04: client mode toggle — the server still FORCES Strict on financial-intent queries (forced_strict).
  const [mode, setMode] = useState<"strict" | "creative">("strict");
  // Live voice conversation overlay (VAD-driven, not tap-to-record-once).
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const voiceTriggerRef = useRef<HTMLElement | null>(null);

  function openVoiceMode() {
    // Remember what had focus so it can be restored on close (WCAG 2.4.3 —
    // keyboard/AT users shouldn't lose their place when a modal closes).
    voiceTriggerRef.current = document.activeElement as HTMLElement | null;
    setVoiceModeOpen(true);
  }

  function closeVoiceMode() {
    setVoiceModeOpen(false);
    voiceTriggerRef.current?.focus?.();
  }
  const stopRef = useRef<(() => void) | null>(null);

  // Auto-scroll to bottom on new content.
  const listEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // Cleanup the stream timer on unmount.
  useEffect(() => () => stopRef.current?.(), []);

  function startNewChat() {
    stopRef.current?.();
    stopRef.current = null;
    setMessages([]);
    setBusy(false);
    setActiveId(null);
    setConversationId(null);   // BQ7-2: start a fresh thread
    setCsatDone(false);
    setDrawerOpen(false);
  }

  // BF4: clicking a saved conversation in the sidebar previously only set activeId (highlight +
  // header title) — it never fetched the thread, so the pane stayed empty. Resumes the thread for
  // real: stops any in-flight stream, points activeId/conversationId at the picked conversation so
  // replies/CSAT continue it rather than starting a new one, and loads its real (owner-scoped)
  // messages.
  function pickConversation(id: string) {
    stopRef.current?.();
    stopRef.current = null;
    setBusy(false);
    setActiveId(id);
    setConversationId(id);
    setCsatDone(false);
    setMessages([]);
    void fetchConversationMessages(id).then(setMessages);
  }

  // BQ7-2: submit a conversation-level CSAT ("was this helpful?"). Optimistic — hide the prompt at once.
  function submitCsat(rating: number) {
    setCsatDone(true);
    if (conversationId) void postConversationCsat(conversationId, rating, { context: "end_of_conversation" });
  }

  function handleSend(text: string) {
    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      createdAt: Date.now(),
    };
    const assistantMsg = makeStreamingPlaceholder();
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setBusy(true);

    // L04: real SSE — POST /api/chat/answer/stream via realApi. Token chunks → onDelta, final
    // answer envelope → onDone (citations/confidence/forced_strict/mode), honest no-key gate →
    // onUnavailable. The mock streamer is retired from this path.
    const controller = new AbortController();
    const patch = (id: string, next: Partial<Message>) =>
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...next } : m)));
    streamChatAnswer(
      { query: text, mode, conversationId: conversationId ?? undefined, signal: controller.signal },
      {
        onDelta: (delta) =>
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id ? { ...m, content: m.content + delta } : m,
            ),
          ),
        onDone: (env) => {
          // BI2: grounded step-flow — when the answer is a "how to / steps" process, the backend
          // returns the prose MINUS the numbered list (env.answer) plus the verbatim steps
          // (env.steps). Render the prose as text and the steps as a visual StepFlow block, so the
          // process shows once (no duplication). Otherwise keep the streamed content as before.
          const wantsSteps =
            env.render === "steps" && Array.isArray(env.steps) && env.steps.length >= 2;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? {
                    ...m,
                    streaming: false,
                    content: wantsSteps
                      ? env.answer || ""
                      : m.content && m.content.trim()
                        ? m.content
                        : env.answer || "",
                    blocks: wantsSteps
                      ? [{ kind: "steps", data: { steps: env.steps ?? [] } }, ...(m.blocks ?? [])]
                      : m.blocks,
                    citations: mapCitations(env.citations),
                    confidence: env.confidence,
                    mode: env.mode,
                    forcedStrict: env.forced_strict,
                    available: env.available,
                    escalated: env.escalated,
                    holding: env.holding,
                  }
                : m,
            ),
          );
          if (env.conversation_id) {
            const isNewThread = env.conversation_id !== conversationId;
            setConversationId(env.conversation_id);  // BQ7-2: real thread id
            if (isNewThread) refreshHistory();       // new thread just persisted — show it in Recent
          }
          setBusy(false);
          stopRef.current = null;
        },
        onUnavailable: (env) => {
          patch(assistantMsg.id, {
            streaming: false,
            content:
              "I can’t answer this yet — the AI provider isn’t configured. " +
              (env.reason || ""),
            available: false,
            reason: env.reason,
            forcedStrict: env.forced_strict,
            mode: env.mode,
            citations: mapCitations(env.citations),
            confidence: env.confidence,
          });
          setBusy(false);
          stopRef.current = null;
        },
        onError: () => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? {
                    ...m,
                    streaming: false,
                    content:
                      m.content && m.content.trim()
                        ? m.content
                        : "Something went wrong reaching the server. Please try again.",
                    available: false,
                  }
                : m,
            ),
          );
          setBusy(false);
          stopRef.current = null;
        },
      },
    );
    stopRef.current = () => controller.abort();
  }

  function handleAttach(file: File) {
    // AH1 — REAL upload flow (replaces the old keyword-routing mock):
    //   1. Append the attachment chip message.
    //   2. Run OCR Pro on the file (POST /api/upload) — the server extracts it and persists to
    //      ocr_extractions_v2 scoped to this user.
    //   3. Only AFTER extraction completes, send the audit turn, so the backend's
    //      get_chat_grounding_ctx injects the freshly-extracted document and the bot audits the
    //      real fields instead of refusing "I'm unable to review documents directly".
    const attachMsg: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: "",
      createdAt: Date.now(),
      attachment: {
        fileName: file.name,
        sizeBytes: file.size,
        mimeType: file.type,
      },
    };
    setMessages((prev) => [...prev, attachMsg]);
    void uploadChatDocument(file).then((res) => {
      if (res.ok && res.partial) {
        // BI1: partial read — the doc WAS uploaded and its raw text is grounded server-side, so
        // still ask the advisor to help using what's readable (never a dead-end "couldn't read it").
        handleSend(
          `I just uploaded ${file.name}${res.summary ? ` (${res.summary})` : ""}. ` +
            `${res.partialNote || "I could only read part of this document."} ` +
            `Using whatever you can read from it, tell me how to proceed and which specific ` +
            `details I should confirm.`,
        );
      } else if (res.ok) {
        handleSend(
          `I just uploaded ${file.name}${res.summary ? ` (${res.summary})` : ""}. ` +
            `Please audit this policy using the extracted details — flag any gaps, sub-limits, ` +
            `waiting periods or room-rent caps I should know about.`,
        );
      } else {
        // Honest failure — never pretend the document was read.
        handleSend(
          `I tried to upload ${file.name} but the extraction did not complete (${res.error || "unknown error"}). ` +
            `What should I check, or can I try a clearer scan?`,
        );
      }
    });
  }

  // Voice conversation overlay callbacks — mirror handleSend's message-list
  // mutations exactly (same Message shape, same envelope fields) so the
  // conversation looks identical whether a turn came from typing or voice.
  function voiceAppendUserMessage(text: string) {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: text, createdAt: Date.now() },
    ]);
  }

  function voiceAppendAssistantPlaceholder(): string {
    const assistantMsg = makeStreamingPlaceholder();
    setMessages((prev) => [...prev, assistantMsg]);
    return assistantMsg.id;
  }

  function voiceAppendAssistantDelta(id: string, delta: string) {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: m.content + delta } : m)));
  }

  function voiceFinalizeAssistantMessage(id: string, env: ChatEnvelope) {
    const wantsSteps = env.render === "steps" && Array.isArray(env.steps) && env.steps.length >= 2;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id
          ? {
              ...m,
              streaming: false,
              content: wantsSteps
                ? env.answer || ""
                : m.content && m.content.trim()
                  ? m.content
                  : env.answer || "",
              blocks: wantsSteps
                ? [{ kind: "steps", data: { steps: env.steps ?? [] } }, ...(m.blocks ?? [])]
                : m.blocks,
              citations: mapCitations(env.citations),
              confidence: env.confidence,
              mode: env.mode,
              forcedStrict: env.forced_strict,
              available: env.available,
              escalated: env.escalated,
              holding: env.holding,
            }
          : m,
      ),
    );
  }

  function voiceMarkAssistantUnavailable(id: string, env: ChatEnvelope) {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id
          ? {
              ...m,
              streaming: false,
              content: "I can’t answer this yet — the AI provider isn’t configured. " + (env.reason || ""),
              available: false,
              reason: env.reason,
              forcedStrict: env.forced_strict,
              mode: env.mode,
              citations: mapCitations(env.citations),
              confidence: env.confidence,
            }
          : m,
      ),
    );
  }

  function voiceOnConversationId(id: string) {
    const isNewThread = id !== conversationId;
    setConversationId(id);
    if (isNewThread) refreshHistory();
  }

  const userName = answers?.name?.trim() || undefined;
  const userEmail =
    answers?.contact?.channel === "email" ? "you@beyondsure.in" : undefined;

  return (
    <div
      className={[
        "h-[100dvh] w-full overflow-hidden",
        // Calm reading surface — warm cream, NOT the dark canvas-green
        // that the auth shell uses. The chat is read for minutes at a
        // time so dark backgrounds tire the eye.
        "bg-[var(--color-paper)] text-[var(--color-ink)]",
        "flex",
      ].join(" ")}
      data-testid="chat-screen"
    >
      {/* Desktop sidebar — hidden on small screens. inert while voice mode is
          open so keyboard/screen-reader users can't navigate into content
          hidden behind the full-screen overlay (docs/VOICE-5-conversation-mode.md). */}
      <div className="hidden sm:flex" inert={voiceModeOpen || undefined}>
        <ChatSidebar
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
          conversations={conversations}
          activeId={activeId}
          onPickConversation={pickConversation}
          onNewChat={startNewChat}
          userName={userName}
          userEmail={userEmail}
          activeRoute="/chat"
          onPickFeatures={() => navigate("/features")}
          onPickDocuments={() => navigate("/documents")}
          onPickOcrPro={() => navigate("/ocr")}
          onPickVoice={() => navigate("/voice")}
          onPickPolicies={() => navigate("/policies")}
          onPickFamily={() => navigate("/family")}
          onPickFeatureSlug={(slug) => navigate(`/features/${slug}`)}
          onPickSearch={() => {
            // Search lives on /features for now — opens the page and the
            // user focuses the search bar with "/". Future: dedicated
            // search modal that spans chats + features.
            navigate("/features");
          }}
        />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <ChatSidebar
          collapsed={false}
          onToggleCollapsed={() => {}}
          conversations={conversations}
          activeId={activeId}
          onPickConversation={(id) => {
            pickConversation(id);
            setDrawerOpen(false);
          }}
          onNewChat={startNewChat}
          userName={userName}
          userEmail={userEmail}
          hideCollapseToggle
          activeRoute="/chat"
          onPickFeatures={() => {
            setDrawerOpen(false);
            navigate("/features");
          }}
          onPickSearch={() => {
            setDrawerOpen(false);
            navigate("/features");
          }}
          onPickDocuments={() => {
            setDrawerOpen(false);
            navigate("/documents");
          }}
          onPickOcrPro={() => {
            setDrawerOpen(false);
            navigate("/ocr");
          }}
          onPickVoice={() => {
            setDrawerOpen(false);
            navigate("/voice");
          }}
          onPickPolicies={() => {
            setDrawerOpen(false);
            navigate("/policies");
          }}
          onPickFamily={() => {
            setDrawerOpen(false);
            navigate("/family");
          }}
          onPickFeatureSlug={(slug) => {
            setDrawerOpen(false);
            navigate(`/features/${slug}`);
          }}
        />
      </MobileDrawer>

      {/* Main column */}
      <main className="flex-1 flex flex-col min-w-0" inert={voiceModeOpen || undefined}>
        {/* Top bar — mobile shows hamburger, desktop is mostly air */}
        <header
          className={[
            "flex items-center gap-2 px-3 sm:px-5",
            "h-12 sm:h-14",
            "border-b border-[rgb(20_28_24_/_0.04)]",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-hamburger"
            aria-label="Open conversation history"
            className={[
              "sm:hidden inline-flex items-center justify-center",
              "h-9 w-9 -ml-1 rounded-[8px]",
              "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
              "transition-colors duration-[var(--duration-fast)]",
            ].join(" ")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className="sm:hidden bsure-display font-bold text-[15px] text-[var(--color-ink)]">
            Beyond Sure
          </span>
          {/* Desktop title placeholder — quiet so it doesn't compete. */}
          <span className="hidden sm:inline text-[13px] text-[var(--color-ink-40)]">
            {activeId
              ? conversations.find((c) => c.id === activeId)?.title ?? "Chat"
              : "New chat"}
          </span>
        </header>

        {/* Messages or empty state */}
        <div
          className={[
            "flex-1 overflow-y-auto",
            "px-3 sm:px-5",
            // Calm near-static gradient (very subtle) — mirrors Claude's
            // composed background.
            "[background-image:radial-gradient(circle_at_50%_-10%,rgba(62,142,91,0.06),transparent_50%)]",
          ].join(" ")}
          data-testid="chat-message-list"
        >
          <div className="mx-auto max-w-[720px] py-6 sm:py-8 flex flex-col gap-5 min-h-full">
            {messages.length === 0 ? (
              <EmptyState
                userName={userName}
                persona={answers?.persona ?? null}
              >
                <StarterPrompts
                  persona={answers?.persona ?? null}
                  onPick={handleSend}
                />
              </EmptyState>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} message={m} />)
            )}
            <div ref={listEndRef} />
          </div>
        </div>

        {/* BQ7-2: conversation-level CSAT — shown once per thread after a real answer, dismissible. */}
        {conversationId &&
          !csatDone &&
          !busy &&
          messages.some((m) => m.role === "assistant" && !m.streaming) && (
            <div className="mx-auto max-w-[720px] w-full px-3 sm:px-5 pb-1.5">
              <div className="flex items-center justify-center gap-2 rounded-xl bg-[var(--color-ink-08)] py-2 text-[12px] text-[var(--color-ink-60)]">
                <span>Was this conversation helpful?</span>
                <button
                  type="button"
                  onClick={() => submitCsat(5)}
                  className="px-2 py-0.5 rounded-full hover:bg-white transition-colors duration-[var(--duration-fast)]"
                  aria-label="Yes, this was helpful"
                >
                  👍 Yes
                </button>
                <button
                  type="button"
                  onClick={() => submitCsat(1)}
                  className="px-2 py-0.5 rounded-full hover:bg-white transition-colors duration-[var(--duration-fast)]"
                  aria-label="No, this was not helpful"
                >
                  👎 No
                </button>
                <button
                  type="button"
                  onClick={() => setCsatDone(true)}
                  className="ml-1 text-[var(--color-ink-40)] hover:text-[var(--color-ink-60)]"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

        {/* Composer */}
        <div className="pt-2">
          {/* L04: Strict / Creative mode toggle — the server still FORCES Strict on financial intent. */}
          <div className="mx-auto max-w-[720px] w-full px-3 sm:px-5 flex items-center justify-end gap-2 pb-1.5">
            <span className="text-[11px] text-[var(--color-ink-40)]">Mode</span>
            <div
              className="inline-flex rounded-full bg-[var(--color-ink-08)] p-0.5"
              role="group"
              aria-label="Answer mode"
            >
              {(["strict", "creative"] as const).map((mo) => (
                <button
                  key={mo}
                  type="button"
                  onClick={() => setMode(mo)}
                  aria-pressed={mode === mo}
                  className={[
                    "px-2.5 py-1 rounded-full text-[11px] font-medium capitalize",
                    "transition-colors duration-[var(--duration-fast)]",
                    mode === mo
                      ? "bg-white text-[var(--color-ink)] shadow-sm"
                      : "text-[var(--color-ink-40)] hover:text-[var(--color-ink-60)]",
                  ].join(" ")}
                >
                  {mo}
                </button>
              ))}
            </div>
          </div>
          <ChatComposer
            onSend={handleSend}
            onAttach={handleAttach}
            busy={busy}
            placeholder={
              answers?.persona && answers.persona !== "customer"
                ? "Ask me about a client's plan, a renewal, or a claim…"
                : "Ask me to compare plans, audit a policy, or explain the fine print…"
            }
            onOpenVoiceConversation={openVoiceMode}
          />
        </div>
      </main>

      {voiceModeOpen ? (
        <VoiceConversationErrorBoundary onClose={closeVoiceMode}>
          <Suspense
            fallback={
              <div
                className="fixed inset-0 z-50 grid place-items-center bg-[var(--color-paper)]"
                role="status"
                aria-live="polite"
              >
                <p className="text-[13px] text-[var(--color-ink-40)]">Loading voice conversation…</p>
              </div>
            }
          >
            <VoiceConversationOverlay
              conversationId={conversationId}
              mode={mode}
              onConversationId={voiceOnConversationId}
              appendUserMessage={voiceAppendUserMessage}
              appendAssistantPlaceholder={voiceAppendAssistantPlaceholder}
              appendAssistantDelta={voiceAppendAssistantDelta}
              finalizeAssistantMessage={voiceFinalizeAssistantMessage}
              markAssistantUnavailable={voiceMarkAssistantUnavailable}
              onClose={closeVoiceMode}
            />
          </Suspense>
        </VoiceConversationErrorBoundary>
      ) : null}
    </div>
  );
}

export default ChatScreen;
